import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';
import {
  Activity,
  PhoneIncoming,
  Loader2,
  CheckCircle2,
  XCircle,
  Pause,
  Clock,
} from 'lucide-react';
import type { CallStatus } from '../../types';

type BadgeStatus = CallStatus | 'live' | 'ringing' | 'processing' | 'ended' | 'error' | 'active' | 'inbound' | 'outbound';

interface StatusBadgeProps {
  status: BadgeStatus;
  showIcon?: boolean;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  pulse?: boolean;
  className?: string;
}

const statusConfig: Record<
  BadgeStatus,
  { label: string; color: string; icon: React.ReactNode; animate: boolean }
> = {
  live: {
    label: 'Live',
    color: 'bg-success/20 text-success border-success/30',
    icon: <Activity className="w-3 h-3" />,
    animate: true,
  },
  active: {
    label: 'Active',
    color: 'bg-success/20 text-success border-success/30',
    icon: <Activity className="w-3 h-3" />,
    animate: true,
  },
  ringing: {
    label: 'Ringing',
    color: 'bg-vokentra/20 text-vokentra border-vokentra/30',
    icon: <PhoneIncoming className="w-3 h-3" />,
    animate: true,
  },
  processing: {
    label: 'Processing',
    color: 'bg-warning/20 text-warning border-warning/30',
    icon: <Loader2 className="w-3 h-3" />,
    animate: true,
  },
  ended: {
    label: 'Ended',
    color: 'bg-white/10 text-text-muted border-white/10',
    icon: <CheckCircle2 className="w-3 h-3" />,
    animate: false,
  },
  error: {
    label: 'Error',
    color: 'bg-error/20 text-error border-error/30',
    icon: <XCircle className="w-3 h-3" />,
    animate: true,
  },
  hold: {
    label: 'On Hold',
    color: 'bg-warning/20 text-warning border-warning/30',
    icon: <Pause className="w-3 h-3" />,
    animate: false,
  },
  queued: {
    label: 'Queued',
    color: 'bg-info/20 text-info border-info/30',
    icon: <Clock className="w-3 h-3" />,
    animate: false,
  },
  inbound: {
    label: 'Inbound',
    color: 'bg-info/20 text-info border-info/30',
    icon: <PhoneIncoming className="w-3 h-3" />,
    animate: false,
  },
  outbound: {
    label: 'Outbound',
    color: 'bg-white/10 text-text-secondary border-white/10',
    icon: <Activity className="w-3 h-3" />,
    animate: false,
  },
};

const sizeStyles = {
  sm: 'px-2 py-0.5 text-[10px] gap-1',
  md: 'px-2.5 py-1 text-xs gap-1.5',
  lg: 'px-3 py-1.5 text-sm gap-2',
};

export function StatusBadge({
  status,
  showIcon = true,
  showLabel = true,
  size = 'md',
  pulse = true,
  className,
}: StatusBadgeProps) {
  const config = statusConfig[status] || statusConfig.ended;

  return (
    <motion.div
      className={cn(
        'inline-flex items-center rounded-full border font-medium',
        sizeStyles[size],
        config.color,
        className
      )}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
    >
      {showIcon && (
        <span className={cn(config.animate && status === 'processing' && 'animate-spin')}>
          {config.icon}
        </span>
      )}
      {showLabel && <span>{config.label}</span>}

      {/* Pulse dot for animated statuses */}
      {config.animate && pulse && (status === 'live' || status === 'active' || status === 'ringing') && (
        <span className="relative flex h-2 w-2 ml-0.5">
          <motion.span
            className={cn(
              'absolute inline-flex h-full w-full rounded-full opacity-75',
              status === 'ringing' ? 'bg-vokentra' : 'bg-success'
            )}
            animate={{
              scale: [1, 1.8, 1],
              opacity: [0.7, 0, 0.7],
            }}
            transition={{
              duration: status === 'ringing' ? 1 : 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          <span
            className={cn(
              'relative inline-flex rounded-full h-2 w-2',
              status === 'ringing' ? 'bg-vokentra' : 'bg-success'
            )}
          />
        </span>
      )}
    </motion.div>
  );
}

// Simple dot indicator
interface StatusDotProps {
  status: 'live' | 'ringing' | 'processing' | 'ended' | 'error';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const dotColors = {
  live: 'bg-success',
  ringing: 'bg-vokentra',
  processing: 'bg-warning',
  ended: 'bg-white/30',
  error: 'bg-error',
};

const dotSizes = {
  sm: 'w-1.5 h-1.5',
  md: 'w-2.5 h-2.5',
  lg: 'w-3.5 h-3.5',
};

export function StatusDot({ status, size = 'md', className }: StatusDotProps) {
  return (
    <span className="relative flex">
      <motion.span
        className={cn(dotColors[status], dotSizes[size], 'rounded-full', className)}
        animate={
          status !== 'ended'
            ? {
                boxShadow: [
                  `0 0 0 0 ${status === 'live' ? 'rgba(0, 208, 132, 0.4)' : status === 'ringing' ? 'rgba(230, 0, 0, 0.4)' : status === 'error' ? 'rgba(255, 51, 102, 0.4)' : 'rgba(255, 165, 0, 0.4)'}`,
                  `0 0 0 6px ${status === 'live' ? 'rgba(0, 208, 132, 0)' : status === 'ringing' ? 'rgba(230, 0, 0, 0)' : status === 'error' ? 'rgba(255, 51, 102, 0)' : 'rgba(255, 165, 0, 0)'}`,
                ],
              }
            : undefined
        }
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
    </span>
  );
}

// Connection status indicator
interface ConnectionStatusProps {
  status: 'connected' | 'connecting' | 'disconnected' | 'error' | 'reconnecting';
  className?: string;
}

export function ConnectionStatus({ status, className }: ConnectionStatusProps) {
  const statusMap = {
    connected: { color: 'bg-success', label: 'Connected' },
    connecting: { color: 'bg-warning', label: 'Connecting...' },
    reconnecting: { color: 'bg-warning', label: 'Reconnecting...' },
    disconnected: { color: 'bg-text-muted', label: 'Disconnected' },
    error: { color: 'bg-error', label: 'Error' },
  };

  const { color, label } = statusMap[status];

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <StatusDot
        status={status === 'connected' ? 'live' : status === 'connecting' ? 'processing' : status === 'error' ? 'error' : 'ended'}
        size="sm"
      />
      <span className="text-xs text-text-secondary">{label}</span>
    </div>
  );
}
